import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiInterface } from '../api-interface';
import { ApiService } from '../api.service';
import {
  ContratacaoDeUsuarios,
  IntencaoInscricao,
  LimiteDeUsuarios,
  SimulacaoContratacaoUsuarios,
} from '../../models/planos/plano.model';

/**
 * Usuários de acesso adicionais: limite do plano, prévia do valor e pagamento à vista por QR.
 *
 * A contratação feita JUNTO com o plano não passa por aqui — é o parâmetro `usuariosExtras`
 * do checkout, em `PlanosService`.
 */
@Injectable({
  providedIn: 'root',
})
export class ContratacaoUsuariosService implements ApiInterface {

  constructor(private apiService: ApiService) {}

  // --- Metodos padrao ApiInterface ---

  get<T>(url: string, data?: any): Observable<any> {
    const queryParams = this.apiService.buildQueryParams(data);
    const path = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<any>(path);
  }

  post<T>(url: string, data: any): Observable<any> {
    return this.apiService.create<any>(url, data);
  }

  put<T>(url: string, data: any): Observable<any> {
    return this.apiService.update<any>(url, data);
  }

  delete<T>(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  // --- Metodos especificos ---

  /** GET /contratacao-usuarios/limite — quantas vagas a empresa tem e quanto custa mais uma. */
  consultarLimite(): Observable<LimiteDeUsuarios> {
    return this.apiService.read<LimiteDeUsuarios>('/contratacao-usuarios/limite');
  }

  /** GET /contratacao-usuarios/simular?quantidade= — prévia do valor antes de gerar o QR. */
  simular(quantidade: number): Observable<SimulacaoContratacaoUsuarios> {
    return this.apiService.read<SimulacaoContratacaoUsuarios>(
      `/contratacao-usuarios/simular?quantidade=${quantidade}`
    );
  }

  /**
   * POST /contratacao-usuarios/qr?quantidade= — gera o QR de pagamento à vista.
   * Devolve a intenção com `qrData`/`urlQr`, igual ao checkout de plano.
   */
  gerarQr(quantidade: number): Observable<IntencaoInscricao> {
    return this.apiService.create<IntencaoInscricao>(
      `/contratacao-usuarios/qr?quantidade=${quantidade}`,
      undefined
    );
  }

  /**
   * POST /contratacao-usuarios/tpago?quantidade= — gera o link de pagamento por cartão.
   * Devolve a intenção com `linkUrl`/`linkAlias`. A cobrança é única: o backend cancela o
   * link assim que o pagamento confirma, para não virar débito recorrente.
   */
  gerarLinkTpago(quantidade: number): Observable<IntencaoInscricao> {
    return this.apiService.create<IntencaoInscricao>(
      `/contratacao-usuarios/tpago?quantidade=${quantidade}`,
      undefined
    );
  }

  /**
   * GET /contratacao-usuarios/verificar-pagamento/{hookAlias} — polling do pagamento.
   * Enquanto não confirma, o backend responde 404 (é o sinal de "ainda não").
   */
  verificarPagamento(hookAlias: string): Observable<ContratacaoDeUsuarios> {
    return this.apiService.read<ContratacaoDeUsuarios>(
      `/contratacao-usuarios/verificar-pagamento/${hookAlias}`
    );
  }

  /** GET /contratacao-usuarios — histórico de contratações da vigência atual. */
  listar(): Observable<ContratacaoDeUsuarios[]> {
    return this.apiService.read<ContratacaoDeUsuarios[]>('/contratacao-usuarios');
  }
}
